import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Menu, ChevronDown, User, Settings, HelpCircle, LogOut, X, Mail, Phone, Building2, Shield, Copy, Check, Pencil } from 'lucide-react';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import NotificationCenter from '../notifications/NotificationCenter';
import logoFull from '../../assets/images/logo_aktivity.png';
import logoIcon from '../../assets/images/standalone_logo.png';

const getAvatarSrc = (avatar) => {
  if (!avatar) return undefined;
  if (avatar.startsWith('http')) return avatar;
  const apiBase = (process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api')).replace(/\/api\/?$/, '');
  if (avatar.startsWith('/')) return `${apiBase}${avatar}`;
  return `${apiBase}/uploads/profiles/${avatar}`;
};

// "Jan 2024" style label for the profile card stats strip. Falls back
// to "—" when createdAt is missing or unparseable.
const formatMemberSince = (createdAt) => {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
};

// Settings page lives under the role's section. Same mapping the
// dropdown menu uses; kept in one place so the profile card and
// dropdown stay in sync.
/**
 * A trainer whose organization is of type "individual" works for themselves
 * rather than for a client, which changes what they can do across the app
 * (their own sessions to edit, reschedule and delete). Name that in the
 * profile menu instead of the bare role.
 */
const getRoleLabel = (user) => {
  const raw = (user?.role || '').replace('-', ' ').replace('_', ' ');
  const org = user?.primaryOrganization || user?.organization;
  if (user?.role === 'trainer' && org?.type === 'individual') return 'Individual Trainer';
  return raw;
};

const getSettingsPathForRole = (role) => {
  switch (role) {
    case 'super-admin': return '/admin/settings';
    case 'client': return '/client/settings';
    case 'trainer': return '/trainer/settings';
    default: return '/';
  }
};

const TopNavigation = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [showHelpCard, setShowHelpCard] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  // Anchor position for the profile dropdown. We portal the dropdown out of
  // the navbar (so it isn't clipped by the navbar's containing-block) and
  // pin it to the trigger via fixed coordinates measured on open + on
  // resize/scroll while open.
  const profileBtnRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState({ top: 0, right: 0 });
  useEffect(() => {
    if (!showProfileMenu) return undefined;
    const update = () => {
      const r = profileBtnRef.current?.getBoundingClientRect();
      if (!r) return;
      // mt-2 in the original styling = 8px gap below the button.
      setMenuAnchor({ top: r.bottom + 8, right: window.innerWidth - r.right });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    // Click-outside to close — we register on capture so it fires before
    // any click handler inside the menu.
    const onDocClick = (e) => {
      if (profileBtnRef.current?.contains(e.target)) return;
      const menu = document.getElementById('profile-dropdown-menu');
      if (menu && menu.contains(e.target)) return;
      setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', onDocClick, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('mousedown', onDocClick, true);
    };
  }, [showProfileMenu]);

  // Refs for the profile modal a11y plumbing — we focus the close
  // button on open and restore focus to whoever opened the modal
  // when it closes.
  const profileCloseBtnRef = useRef(null);
  const profileTriggerRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const loadUser = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (!parsedUser.fullName && (parsedUser.firstName || parsedUser.lastName)) {
          parsedUser.fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
        }
        if (!parsedUser.fullName && parsedUser.name) {
          parsedUser.fullName = parsedUser.name;
        }
        setUser(parsedUser);
      }
    };
    loadUser();
    window.addEventListener('user-updated', loadUser);
    window.addEventListener('storage', loadUser);
    return () => {
      window.removeEventListener('user-updated', loadUser);
      window.removeEventListener('storage', loadUser);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('current_participant');

    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('training_session_') || key.startsWith('welcome_shown_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    sessionStorage.clear();
    window.history.replaceState(null, '', '/login');
    navigate('/login', { replace: true });
  };

  // ESC closes the profile modal; focus jumps to the close button on
  // open and restores to whatever was focused before opening on close.
  useEffect(() => {
    if (!showProfileCard) return undefined;
    profileTriggerRef.current = document.activeElement;
    const t = window.setTimeout(() => profileCloseBtnRef.current?.focus(), 0);
    const handleKey = (e) => {
      if (e.key === 'Escape') setShowProfileCard(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', handleKey);
      profileTriggerRef.current?.focus?.();
    };
  }, [showProfileCard]);

  const handleCopyPhone = async () => {
    if (!user?.phone) return;
    try {
      await navigator.clipboard.writeText(user.phone);
      setCopiedPhone(true);
      window.setTimeout(() => setCopiedPhone(false), 1200);
    } catch {
      // Clipboard API can be blocked (insecure context, permissions);
      // fail silently — the modal still shows the phone number.
    }
  };

  // Stats strip is dynamic — only Active is unconditional. Member and
  // Sessions only show when the user actually has those values, so the
  // strip never renders empty cells.
  const profileStats = [
    { label: 'Status', value: 'Active' },
    user?.createdAt && { label: 'Member', value: formatMemberSince(user.createdAt) },
    (user?.stats?.sessions ?? null) !== null && { label: 'Sessions', value: user.stats.sessions },
  ].filter(Boolean);
  // Tailwind needs literal class names — can't interpolate into the
  // grid utility, so map count → class.
  const profileStatsGridCls = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' }[profileStats.length] || 'grid-cols-3';

  return (
    <header className="bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800 h-16 flex items-center px-4 sm:px-6 lg:px-8 shadow-nav z-10 transition-colors duration-200">
      <div className="flex items-center justify-between w-full">
        {/* Left: Menu button + Logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-xl text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-600 transition-all duration-200"
          >
            <Menu size={22} strokeWidth={1.75} />
          </button>

          <div className="flex items-center gap-1">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <img src={logoFull} alt="Aktivity" className="h-9 object-contain hidden sm:block" />
          </div>
        </div>

        {/* Right: Notifications + Profile */}
        <div className="flex items-center gap-2">
          <NotificationCenter />

          <div className="relative">
            <button
              ref={profileBtnRef}
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all duration-200"
            >
              <Avatar src={getAvatarSrc(user?.avatar)} name={user?.fullName || 'User'} status="online" size="sm" />
              <div className="hidden md:block text-left">
                <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 leading-tight">{user?.fullName || 'User'}</p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 capitalize">{getRoleLabel(user)}</p>
              </div>
              <ChevronDown size={16} className="text-neutral-400 hidden md:block" />
            </button>

            {/* Dropdown is portaled into <body> so the navbar's containing
                block (and any ancestor with overflow/transform/filter) can't
                clip it — the previous absolute-positioned version was getting
                cut mid-card on some layouts. */}
            {createPortal(
            <AnimatePresence>
              {showProfileMenu && (
                <motion.div
                  id="profile-dropdown-menu"
                  initial={{ opacity: 0, y: -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                  style={{ top: menuAnchor.top, right: menuAnchor.right }}
                  className="fixed w-64 bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl ring-1 ring-black/5 border border-neutral-200 dark:border-neutral-700 overflow-hidden z-[60]"
                >
                  {/* User Info */}
                  <div className="p-4 border-b border-neutral-100 dark:border-neutral-700">
                    <p className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">{user?.fullName}</p>
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">{user?.email}</p>
                    <Badge variant="primary" size="sm" className="mt-2 capitalize">
                      {getRoleLabel(user)}
                    </Badge>
                  </div>

                  {/* Menu Items — hidden for participants (they don't have settings/help pages) */}
                  {user?.role !== 'participant' && (
                  <div className="py-1.5">
                    <button
                      onClick={() => { setShowProfileCard(true); setShowProfileMenu(false); }}
                      className="w-full px-4 py-2.5 text-left text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-150 flex items-center gap-3"
                    >
                      <User size={18} strokeWidth={1.75} className="text-neutral-400" />
                      My Profile
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        const role = user?.role;
                        if (role === 'super-admin') navigate('/admin/settings');
                        else if (role === 'client') navigate('/client/settings');
                        else if (role === 'trainer') navigate('/trainer/settings');
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-150 flex items-center gap-3"
                    >
                      <Settings size={18} strokeWidth={1.75} className="text-neutral-400" />
                      Settings
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        const role = user?.role;
                        if (role === 'client') navigate('/client/help');
                        else if (role === 'trainer') navigate('/trainer/help');
                        else setShowHelpCard(true); // super-admin: no help page, show card
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-150 flex items-center gap-3"
                    >
                      <HelpCircle size={18} strokeWidth={1.75} className="text-neutral-400" />
                      Help & Support
                    </button>
                  </div>
                  )}

                  {/* Logout */}
                  <div className="border-t border-neutral-100 dark:border-neutral-700 p-1.5">
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2.5 text-left text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150 flex items-center gap-3 rounded-xl"
                    >
                      <LogOut size={18} strokeWidth={1.75} />
                      Logout
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>,
            document.body
            )}
          </div>
        </div>
      </div>

      {/* Profile Card Modal — rendered via portal directly into <body> so
          ancestor `transform`/`filter`/`overflow` styles (e.g. on the layout
          flex column or framer-motion siblings) can't establish a containing
          block that breaks the fixed centering. */}
      {createPortal(
      <AnimatePresence>
        {showProfileCard && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setShowProfileCard(false)}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neutral-950/40 backdrop-blur-md"
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="profile-card-title"
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 12 }}
              animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 12 }}
              transition={prefersReducedMotion ? { duration: 0.15 } : { type: 'spring', damping: 24, stiffness: 320 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white dark:bg-neutral-900 rounded-3xl ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_20px_60px_-20px_rgba(16,24,40,0.30),0_8px_24px_-8px_rgba(16,24,40,0.12)] w-full max-w-[380px] overflow-hidden"
            >
              {/* Visual identity comes from the hero + avatar + name —
                  no visible title row. Keep an sr-only h3 so screen
                  readers still announce the modal's purpose. */}
              <h3 id="profile-card-title" className="sr-only">My Profile</h3>

              {/* Hero banner — brand gradient with two soft white rings
                  for depth. Holds the close button. */}
              <div className="relative h-20 bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800 overflow-hidden">
                <div aria-hidden="true" className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 ring-1 ring-inset ring-white/10 pointer-events-none" />
                <div aria-hidden="true" className="absolute -bottom-20 -left-12 w-40 h-40 rounded-full bg-white/5 ring-1 ring-inset ring-white/10 pointer-events-none" />
                <button
                  ref={profileCloseBtnRef}
                  onClick={() => setShowProfileCard(false)}
                  aria-label="Close profile"
                  className="absolute top-3 right-3 p-1.5 rounded-lg text-white/80 hover:text-white bg-white/10 hover:bg-white/20 ring-1 ring-inset ring-white/15 backdrop-blur-sm transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Avatar — half on the banner, half on the card surface.
                  Tinted gradient padding frames the avatar so the
                  initials sit on the brand color, not plain gray. */}
              <div className="-mt-12 flex justify-center">
                <div className="relative">
                  <div className="rounded-full p-[3px] bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-500/20 dark:to-primary-700/20 ring-4 ring-white dark:ring-neutral-900 shadow-[0_8px_20px_-6px_rgba(16,24,40,0.25)]">
                    <Avatar
                      src={getAvatarSrc(user?.avatar)}
                      name={user?.fullName}
                      size="xl"
                      className="bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 dark:from-primary-500/20 dark:to-primary-700/20 dark:text-primary-200"
                    />
                  </div>
                  {/* Edit-profile pencil — top-right corner, opposite the
                      online dot. Reuses the same role→settings helper as
                      the footer Settings button. */}
                  <button
                    onClick={() => {
                      setShowProfileCard(false);
                      navigate(getSettingsPathForRole(user?.role));
                    }}
                    aria-label="Edit profile"
                    className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-700 shadow-md flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:text-primary-600 hover:ring-primary-300 transition-colors"
                  >
                    <Pencil size={12} strokeWidth={2} />
                  </button>
                  <span aria-hidden="true" className="absolute bottom-1 right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-neutral-900" />
                </div>
              </div>

              {/* Identity — name, email, role badge */}
              <div className="text-center mt-3 px-5">
                <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 tracking-tight">
                  {user?.fullName}
                </h4>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                  {user?.email}
                </p>
                <Badge
                  variant="primary"
                  size="sm"
                  className="mt-2 capitalize ring-1 ring-inset ring-primary-200 dark:ring-primary-500/30 bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-300"
                >
                  <Shield size={11} className="inline mr-1 -mt-0.5" />
                  {getRoleLabel(user)}
                </Badge>
              </div>

              {/* Stats strip — only renders cells with real values, so
                  the layout never shows placeholder dashes. */}
              {profileStats.length > 0 && (
                <div className={`mx-5 mt-4 grid ${profileStatsGridCls} rounded-2xl bg-neutral-50 dark:bg-neutral-800/60 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 divide-x divide-neutral-200/70 dark:divide-neutral-800`}>
                  {profileStats.map((s) => (
                    <div key={s.label} className="px-2 py-3 text-center">
                      <p className="text-[10px] uppercase tracking-[0.08em] text-neutral-400 dark:text-neutral-500 font-medium">{s.label}</p>
                      <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums mt-0.5">{s.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Detail rows — Phone (with copy), Organization, Role.
                  Email is intentionally not here — it lives in the
                  identity block above. */}
              <div className="px-5 pt-4 pb-5 space-y-2">
                {user?.phone && (
                  <button
                    onClick={handleCopyPhone}
                    aria-label={copiedPhone ? 'Phone copied' : 'Copy phone number'}
                    className="group w-full flex items-center gap-3 p-3 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors text-left"
                  >
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-inset ring-emerald-100 dark:ring-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                      <Phone size={16} strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0 flex-1 text-left">
                      <p className="text-[11px] uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500 font-medium">Phone</p>
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{user.phone}</p>
                    </div>
                    <span
                      aria-hidden="true"
                      className={`flex-shrink-0 transition-opacity ${copiedPhone ? 'opacity-100 text-emerald-500' : 'opacity-0 group-hover:opacity-100 text-neutral-400 dark:text-neutral-500'}`}
                    >
                      {copiedPhone ? <Check size={14} /> : <Copy size={14} />}
                    </span>
                  </button>
                )}

                {(user?.primaryOrganization?.name || user?.organization?.name) && (
                  <div className="w-full flex items-center gap-3 p-3 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800">
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-violet-50 dark:bg-violet-500/10 ring-1 ring-inset ring-violet-100 dark:ring-violet-500/20 text-violet-600 dark:text-violet-400 flex-shrink-0">
                      <Building2 size={16} strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0 flex-1 text-left">
                      <p className="text-[11px] uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500 font-medium">Organization</p>
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{user.primaryOrganization?.name || user.organization?.name}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action footer — settings + sign out. Uses the same
                  logout handler as the dropdown so we don't fork
                  cleanup logic. */}
              <div className="px-5 pb-5 pt-1 grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    setShowProfileCard(false);
                    navigate(getSettingsPathForRole(user?.role));
                  }}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors"
                >
                  <Settings size={14} /> Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-1 ring-inset ring-rose-200 dark:ring-rose-500/20 hover:bg-rose-100 dark:hover:bg-rose-500/15 transition-colors"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>,
      document.body
      )}

      {/* Help & Support Card Modal — also portaled for the same reason. */}
      {createPortal(
      <AnimatePresence>
        {showHelpCard && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setShowHelpCard(false)}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/30"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white dark:bg-neutral-800 rounded-2xl shadow-xl border border-neutral-200 dark:border-neutral-700 w-full max-w-sm overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-neutral-100 dark:border-neutral-700">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Help & Support</h3>
                <button
                  onClick={() => setShowHelpCard(false)}
                  className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                >
                  <X size={18} className="text-neutral-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-center w-14 h-14 bg-primary-50 dark:bg-primary-900/30 rounded-2xl mx-auto">
                  <HelpCircle size={28} className="text-primary-600" />
                </div>

                <div className="text-center">
                  <h4 className="text-base font-semibold text-neutral-900 dark:text-white mb-1">Need help?</h4>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Our support team is here to assist you with any questions about the platform, your sessions, or billing.
                  </p>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-primary-600 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">Email us at</p>
                      <a
                        href="mailto:support@aktivity.io"
                        className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        support@aktivity.io
                      </a>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
                  We typically respond within 24 hours on business days.
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>,
      document.body
      )}
    </header>
  );
};

export default TopNavigation;
